import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Mail, Phone, Stethoscope, FileText, CheckCircle, AlertCircle, ArrowLeft, Shield, Lock, Eye, EyeOff } from 'lucide-react';
import { api } from '../api/greenclub';
const ClinicaRegister = () => {
    const [formData, setFormData] = useState({
        nome: '',
        email: '',
        telefone: '',
        crm: '',
        especialidade: '',
        observacoes: '',
        senha: '',
        confirmarSenha: ''
    }) ;
    const [loading, setLoading] = useState(false) ;
    const [submitted, setSubmitted] = useState(false) ;
    const [error, setError] = useState('') ;
    const [showPassword, setShowPassword] = useState(false) ;
    const [showConfirmPassword, setShowConfirmPassword] = useState(false) ;
    const handleSubmit = async (e) => {
        e.preventDefault() ;
        setLoading(true) ;
        setError('') ;
        try {
            // Validações
            if (!formData.nome || !formData.email || !formData.telefone || !formData.crm || !formData.senha || !formData.confirmarSenha) {
                setError('Por favor, preencha todos os campos obrigatórios') ;
                return;
            }
            if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
                setError('Por favor, insira um email válido') ;
                return;
            }
            if (formData.senha.length < 6) {
                setError('A senha deve ter pelo menos 6 caracteres') ;
                return;
            }
            if (formData.senha !== formData.confirmarSenha) {
                setError('As senhas não coincidem') ;
                return;
            }
            const registrationData = {
                ...formData,
                tipo: 'medico',
            };
            const response = await api.createPublicRegistration(registrationData) ;
            if (response.success) {
                setSubmitted(true) ;
            }
            else {
                setError(response.error || 'Erro ao enviar solicitação') ;
            }
        }
        catch (error) {
            console.error('Erro ao enviar cadastro:', error) ;
            setError('Erro ao enviar solicitação. Tente novamente.') ;
        }
        finally {
            setLoading(false) ;
        }
    };
    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        })) ;
    };
    if (submitted) {
        return (<motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-emerald-600"/>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Solicitação Enviada!
          </h2>
          <p className="text-gray-600 mb-6">
            O cadastro da sua clínica foi enviado com sucesso. Nossa equipe validará a documentação e você receberá um email com o resultado em até 24 horas.
          </p>
          <div className="space-y-3">
            <motion.a href="/" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="w-full bg-emerald-600 text-white py-3 rounded-lg font-medium hover:bg-emerald-700 transition-colors inline-block">
              Voltar ao Login
            </motion.a>
            <motion.button onClick={() => {
                setSubmitted(false) ;
                setFormData({
                    nome: '',
                    email: '',
                    telefone: '',
                    crm: '',
                    especialidade: '',
                    observacoes: '',
                    senha: '',
                    confirmarSenha: ''
                }) ;
            }} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors">
              Novo Cadastro
            </motion.button>
          </div>
        </div>
      </motion.div>) ;
    }
    return (<motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <motion.a href="/" whileHover={{ x: -5 }} className="inline-flex items-center text-emerald-700 hover:text-emerald-800 transition-colors mb-4">
            <ArrowLeft className="w-4 h-4 mr-2"/>
            Voltar ao Login
          </motion.a>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-8 text-white">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <Stethoscope className="w-8 h-8"/>
              </div>
              <div>
                <h1 className="text-3xl font-bold mb-2">Cadastro da Clínica</h1>
                <p className="text-emerald-100">
                  Traga sua clínica para o ecossistema GreenClub e conecte-se a pacientes com segurança total
                </p>
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div className="p-6 bg-emerald-50 border-b">
            <h3 className="font-semibold text-emerald-800 mb-4 flex items-center">
              <Shield className="w-5 h-5 mr-2"/>
              Benefícios para clínicas parceiras:
            </h3>
            <div className="grid md:grid-cols-1 gap-3 text-sm text-emerald-700">
              <div>• Plataforma exclusiva e segura – ambiente desenvolvido especialmente para clínicas e pacientes</div>
              <div>• Dashboard com métricas detalhadas da operação</div>
              <div>• Conformidade regulatória e proteção de dados – segurança total em cada etapa do processo</div>
              <div>• Notificações automáticas – receba alertas em tempo real sobre prescrições e acompanhamento dos pacientes</div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            {/* Error Message */}
            <AnimatePresence>
              {error && (<motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0"/>
                  <p className="text-red-800 text-sm">{error}</p>
                </motion.div>)}
            </AnimatePresence>

            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome Completo *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5"/>
                  <input type="text" value={formData.nome} onChange={(e) => handleChange('nome', e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" placeholder="Dr. João Silva" required/>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Profissional *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5"/>
                  <input type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" placeholder="joao.silva@hospital.com.br" required/>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Telefone/WhatsApp *
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5"/>
                <input type="tel" value={formData.telefone} onChange={(e) => handleChange('telefone', e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" placeholder="(11) 99999-9999" required/>
              </div>
            </div>

            {/* Medical Info */}
            <div className="space-y-4 p-6 bg-emerald-50 rounded-lg">
              <h3 className="font-medium text-emerald-800 flex items-center">
                <Stethoscope className="w-5 h-5 mr-2"/>
                Informações Profissionais
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CRM do responsável técnico *
                  </label>
                  <input type="text" value={formData.crm} onChange={(e) => handleChange('crm', e.target.value)} className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" placeholder="CRM/SP 123456" required/>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Especialidade do responsável
                  </label>
                  <input type="text" value={formData.especialidade} onChange={(e) => handleChange('especialidade', e.target.value)} className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" placeholder="Ex: Neurologia, Oncologia, Dor Crônica"/>
                </div>
              </div>
            </div>

            {/* Senha */}
            <div className="space-y-4 p-6 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-800 flex items-center">
                <Lock className="w-5 h-5 mr-2"/>
                Definir Senha de Acesso
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Senha *
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5"/>
                    <input type={showPassword ? "text" : "password"} value={formData.senha} onChange={(e) => handleChange('senha', e.target.value)} className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" placeholder="Mínimo 6 caracteres" required/>
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPassword ? <EyeOff className="w-5 h-5"/> : <Eye className="w-5 h-5"/>}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirmar Senha *
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5"/>
                    <input type={showConfirmPassword ? "text" : "password"} value={formData.confirmarSenha} onChange={(e) => handleChange('confirmarSenha', e.target.value)} className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" placeholder="Repita a senha" required/>
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showConfirmPassword ? <EyeOff className="w-5 h-5"/> : <Eye className="w-5 h-5"/>}
                    </button>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Após aprovação do cadastro, você poderá fazer login com seu email e senha.
              </p>
            </div>

            {/* Observações */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Experiência com Cannabis Medicinal
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 text-gray-400 w-5 h-5"/>
                <textarea value={formData.observacoes} onChange={(e) => handleChange('observacoes', e.target.value)} rows={4} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none" placeholder="Conte sobre sua experiência ou interesse em cannabis medicinal, casos que pretende tratar, cursos realizados, etc..."/>
              </div>
            </div>

            {/* Submit Button */}
            <div>
              <motion.button type="submit" disabled={loading} whileHover={{ scale: loading ? 1 : 1.02 }} whileTap={{ scale: loading ? 1 : 0.98 }} className="w-full bg-emerald-600 text-white py-4 rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2">
                {loading ? (<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>) : (<>
                    <Stethoscope className="w-5 h-5"/>
                    <span>Enviar Cadastro Médico</span>
                  </>)}
              </motion.button>
            </div>

            <div className="text-center text-sm text-gray-600">
              <p>
                Ao enviar o cadastro, você concorda que suas informações profissionais serão verificadas. 
                <br />
                O processo de aprovação leva até 24 horas.
              </p>
            </div>
          </form>
        </div>
      </div>
    </motion.div>) ;
};
export default ClinicaRegister;
